import { useCallback, useState, useMemo } from "react";
import Joi from "joi";
import { object, func } from "prop-types";

const useForm = (initialForm, schema, handleSubmit) => {
  const [data, setData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const handleReset = useCallback(() => {
    setData(initialForm);
    setErrors({});
  }, [initialForm]);

  const validateProperty = useCallback(
    ({ name, value }) => {
      if (!schema[name]) {
        console.error(`Invalid schema for property "${name}"`);
        return null;
      }
      const obj = { [name]: value };
      const generateSchema = Joi.object({ [name]: schema[name] });
      const { error } = generateSchema.validate(obj);
      return error ? error.details[0] : null;
    },
    [schema]
  );
  const handleInputChange = useCallback(
    ({ target }) => {
      const { name, value } = target;
      const errorMessage = validateProperty(target);
      if (errorMessage)
        setErrors((prev) => ({ ...prev, [name]: errorMessage }));
      else
        setErrors((prev) => {
          let obj = { ...prev };
          delete obj[name];
          return obj;
        });

      setData((prev) => ({ ...prev, [name]: value }));
    },
    [validateProperty]
  );
  const validateForm = useCallback(() => {
    const schemaForValidate = Joi.object(schema);
    const { error } = schemaForValidate.validate(data);
    if (error) return error;
    return null;
  }, [schema, data]);

  // #51. The submit result is RETURNED, not dropped, so a caller that awaits
  // `onSubmit` stays awaited for as long as `handleSubmit` runs. LoginPage's
  // own `handleSubmit` (users/pages/LoginPage.jsx:77-82) does
  // `setSubmitting(true); await onSubmit(e); setSubmitting(false);` and the
  // Sign-in button is `disabled={submitting}` (line 425); while this callback
  // returned undefined that await settled on the next microtask, re-arming the
  // button while the sign-in request was still open.
  //
  // Deliberately NOT `async`: that would also turn a SYNCHRONOUS throw from
  // `handleSubmit` into a rejected promise, and `Form.jsx:60` calls
  // `onSubmit(e)` without awaiting it, so such a throw would become an
  // unhandled rejection instead of the error it is today.
  const onSubmit = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    const schemaForValidate = Joi.object(schema);
    const { error } = schemaForValidate.validate(data, { abortEarly: false });
    if (error) {
      const newErrors = {};
      error.details.forEach((detail) => {
        const key = detail.path[detail.path.length - 1];
        if (key && !newErrors[key]) newErrors[key] = detail;
      });
      setErrors(newErrors);
      return;
    }
    setErrors({});
    return handleSubmit(data);
  }, [handleSubmit, data, schema]);

  const handlePublish = () => {
    const error = validateForm();
    if (error) setErrors((prev) => ({ ...prev, ...error }));
    else handleSubmit(data);
  };

  const value = useMemo(() => {
    return { data, errors };
  }, [data, errors]);

  return {
    value,
    onSubmit,
    handleInputChange,
    handleReset,
    validateForm,
    setData,
    handlePublish,
  };
};
useForm.propTypes = {
  initialForm: object.isRequired,
  schema: object.isRequired,
  handleSubmit: func.isRequired,
};

export default useForm;
